import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Eye, EyeOff } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import ApiService from "../../services/api";

const Login: React.FC = () => {
  const navigate = useNavigate();
  const { login: authLogin } = useAuth();
  // const primaryColor = "#2563eb";
  // const brandName = "Gattaca Genomics";
  // const logoUrl = "/assets/logo.svg";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");

  const validateEmail = (value: string) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(value);
  };

  const validatePassword = (value: string) => {
    return value.trim().length >= 6;
  };

  const handleInitialLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setEmailError("");
    setPasswordError("");

    let hasError = false;

    if (!validateEmail(email)) {
      setEmailError("Please enter a valid email address");
      hasError = true;
    }

    if (!validatePassword(password)) {
      setPasswordError("Password must be at least 6 characters");
      hasError = true;
    }

    if (hasError) return;

    setIsLoading(true);

    try {
      const data = await ApiService.login(email, password);

      if (!data?.token?.length) {
        throw new Error(data.message || "Login failed");
      }

      // Store token and login
      localStorage.setItem("token", data.token);

      await authLogin(data.data); // Update auth context

      navigate("/records");
    } catch (err: any) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Invalid email or password"
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center px-4 py-8 sm:px-6 lg:px-8">
        <div className="max-w-lg w-full space-y-8 bg-white p-3 lg:p-6 sm:p-8 rounded-lg shadow-lg border border-gray-200">
          {/* Logo */}
          <div className="text-center">
            <div className="mx-auto h-14 w-14 sm:h-22 sm:w-22 rounded-lg flex items-center justify-center">
              <img
                src="/assets/logo.svg"
                alt="Gattaca Genomics Logo"
                className="h-full w-full"
              />
            </div>
            <h2 className="mt-4 sm:mt-6 text-xl sm:text-2xl font-bold text-gray-900">
              Embryo Data Viewer
            </h2>
            <p className="mt-2 text-xs sm:text-sm text-gray-600">
              Login to your account
            </p>
          </div>

          {/* Form */}
          <form
            // className="mt-6 sm:mt-8 space-y-4 sm:space-y-6"
            className="max-w-md  space-y-6 py-4"
            onSubmit={handleInitialLogin}
          >
            <div className="space-y-3 sm:space-y-4">
              <div>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (emailError && validateEmail(e.target.value)) {
                      setEmailError("");
                    }
                  }}
                  className={`appearance-none relative block w-full px-3 py-2.5 sm:py-3 border ${
                    emailError ? "border-red-500" : "border-gray-300"
                  } placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-300 focus:border-gray-300 focus:z-10 text-sm sm:text-base`}
                  placeholder="Enter your email"
                  autoComplete="username"
                />
                {emailError && (
                  <p className="text-red-600 text-xs sm:text-sm mt-1">
                    {emailError}
                  </p>
                )}
              </div>
              <div className="relative w-full">
                <input
                  type={showPassword ? "text" : "password"}
                  required
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (passwordError && validatePassword(e.target.value)) {
                      setPasswordError("");
                    }
                  }}
                  className={` block w-full px-3 py-3 border ${
                    passwordError ? "border-red-500" : "border-gray-300"
                  } placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-300 focus:border-gray-300 focus:z-10 text-sm sm:text-base pr-10`}
                  placeholder="••••••••"
                  autoComplete="current-password"
                />
                {/* ICON TOGGLE BUTTON */}
                <div
                  style={{
                    top: "19px",
                  }}
                  className="absolute right-3 flex items-center cursor-pointer text-gray-300"
                >
                  {showPassword ? (
                    <EyeOff size={18} onClick={() => setShowPassword(false)} />
                  ) : (
                    <Eye size={18} onClick={() => setShowPassword(true)} />
                  )}
                </div>
                {passwordError && (
                  <p className="text-red-600 text-[10px] sm:text-sm absolute lg:top-13  ">
                    {passwordError}
                  </p>
                )}
              </div>
            </div>

            {error && (
              <div className="text-red-600 text-xs sm:text-sm text-center">
                {error}
              </div>
            )}

            <div>
              <button
                type="submit"
                disabled={isLoading}
                className="group relative w-full flex justify-center py-2.5 sm:py-3 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? "Signing in..." : "Sign in"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;
